import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Steps,
  Button,
  Form,
  message,
  Typography,
  Space,
  Alert,
  Spin,
  Result,
  Select,
  Progress
} from 'antd';
import {
  FileTextOutlined,
  UserOutlined,
  HeartOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import moment from 'moment';
import PublicGeneralHealthAssessmentForm from '../components/PublicGeneralHealthAssessmentForm';
import PublicSurveyLifestyleQuestionnaires from '../components/PublicSurveyLifestyleQuestionnaires';
import {
  validateGeneralHealthAssessment,
  validateLifestyleQuestionnaires,
  calculateCompletionStatus,
  getGeneralHealthRequiredFields,
  getLifestyleRequiredFields,
  addVisualIndicators
} from '../utils/healthSurveyValidation';
import { useLifestyleCategoriesI18n } from '../utils/lifestyleCategoriesI18n';

const { Content, Header } = Layout;
const { Title, Text } = Typography;
const { Step } = Steps;
const { Option } = Select;

const PublicSurveyPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [surveyData, setSurveyData] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [generalHealthData, setGeneralHealthData] = useState({});
  const [lifestyleData, setLifestyleData] = useState({});
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [completionStatus, setCompletionStatus] = useState({ completed: 0, total: 0, percentage: 0 });

  // Get lifestyle categories for validation
  const lifestyleCategories = useLifestyleCategoriesI18n();

  // Add visual indicators when step changes
  useEffect(() => {
    addVisualIndicators('public-survey-page');
  }, [currentStep]);

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setError('Invalid survey link');
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Set language based on survey settings
    if (surveyData?.settings?.language) {
      i18n.changeLanguage(surveyData.settings.language);
    }
  }, [surveyData, i18n]);

  const validateToken = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/public/survey/${token}`);
      
      if (response.data.status === 'success') {
        setSurveyData(response.data.data.survey);
        
        // Check if already completed and multiple submissions not allowed
        if (response.data.data.survey.isCompleted && !response.data.data.survey.allowMultipleSubmissions) {
          setError('This survey has already been completed and multiple submissions are not allowed.');
        }
      }
    } catch (error) {
      console.error('Error validating token:', error);
      if (error.response?.status === 404) {
        setError('Survey link not found or has been deactivated.');
      } else if (error.response?.status === 400) {
        setError(error.response.data.message || 'Survey link is no longer available.');
      } else {
        setError('Failed to load survey. Please check your link and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      // Validate general health assessment with enhanced validation
      const result = await validateGeneralHealthAssessment(form, t);

      if (result.success) {
        setGeneralHealthData(result.values);
        setValidationErrors([]);
        setCurrentStep(1);

        // Update completion status
        const generalRequiredFields = getGeneralHealthRequiredFields();
        const status = calculateCompletionStatus(result.values, generalRequiredFields);
        setCompletionStatus(status);
      } else {
        setValidationErrors(result.missingFields || []);
      }
    }
  };

  const [lifestyleFormRef, setLifestyleFormRef] = useState(null);

  const handlePrev = () => {
    if (currentStep === 1 && lifestyleFormRef) {
      // Capture current lifestyle form data before going back
      try {
        const currentFormData = lifestyleFormRef.getFieldsValue();
        if (currentFormData && Object.keys(currentFormData).length > 0) {
          setLifestyleData(currentFormData);
        }
      } catch (error) {
        console.warn('Could not capture lifestyle form data:', error);
      }
    }
    setCurrentStep(currentStep - 1);
  };

  const handleLifestyleSubmit = async (lifestyleFormData, lifestyleForm) => {
    if (lifestyleForm) {
      // Validate lifestyle questionnaires with enhanced validation
      const result = await validateLifestyleQuestionnaires(lifestyleForm, lifestyleCategories, t);

      if (result.success) {
        // Transform the data to standardized format
        const standardizedData = transformLifestyleData(result.values);
        setLifestyleData(standardizedData);
        setValidationErrors([]);
        handleFinalSubmit(standardizedData);
      } else {
        setValidationErrors(result.missingFields || []);
        // Don't proceed with submission if validation fails
      }
    } else {
      // Direct data submission (backward compatibility)
      setLifestyleData(lifestyleFormData);
      handleFinalSubmit(lifestyleFormData);
    }
  };

  const transformLifestyleData = (values) => {
    const standardizedData = {};

    // Process each lifestyle category
    lifestyleCategories.forEach(category => {
      const categoryData = values[category.key] || {};

      // Transform each parameter in the category
      category.parameters.forEach(param => {
        if (param.type !== 'Header' && categoryData[param.key] !== undefined) {
          const standardizedKey = `lifestyle_${category.key}_${param.key}`;
          let value = categoryData[param.key];

          // Handle BooleanCheckbox type - convert array to object
          if (param.type === 'BooleanCheckbox' && Array.isArray(value)) {
            const booleanObj = {};
            if (param.options) {
              param.options.forEach(option => {
                booleanObj[option.value] = value.includes(option.value);
              });
            }
            value = booleanObj;
          }

          standardizedData[standardizedKey] = value;
        }
      });
    });

    return standardizedData;
  };

  const handleFinalSubmit = async (lifestyleFormData = lifestyleData) => {
    setSubmitting(true);
    try {
      const submissionData = {
        generalHealth: generalHealthData,
        lifestyle: lifestyleFormData
      };

      const response = await axios.post(
        `/public/survey/${token}/submit`,
        submissionData
      );

      if (response.data.status === 'success') {
        setCompleted(true);
        message.success(t('healthSurvey.surveySubmitted'));
      } else {
        throw new Error(response.data.message || 'Failed to submit survey');
      }
    } catch (error) {
      console.error('Error submitting survey:', error);
      message.error(error.response?.data?.message || t('healthSurvey.surveyFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
  };

  const steps = [
    {
      title: t('healthSurvey.generalHealthAssessment'),
      content: t('healthSurvey.generalHealthContent'),
      icon: <UserOutlined />,
    },
    {
      title: t('healthSurvey.lifestyleQuestionnaires'),
      content: t('healthSurvey.lifestyleContent'),
      icon: <HeartOutlined />,
    },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <PublicGeneralHealthAssessmentForm
            form={form}
            initialData={generalHealthData}
          />
        );
      case 1:
        return (
          <PublicSurveyLifestyleQuestionnaires
            patient={surveyData?.patient}
            initialData={lifestyleData}
            onSubmit={handleLifestyleSubmit}
            onCancel={handlePrev}
            onFormRef={setLifestyleFormRef}
            loading={submitting}
          />
        );
      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (currentStep === 1) {
      // Lifestyle questionnaires component handles its own footer
      return null;
    }

    return (
      <div style={{ textAlign: 'right', marginTop: 24 }}>
        <Space>
          {currentStep > 0 && (
            <Button onClick={handlePrev} size="large">
              {t('healthSurvey.previous')}
            </Button>
          )}
          <Button
            type="primary"
            onClick={handleNext}
            size="large"
          >
            {t('healthSurvey.next')}
          </Button>
        </Space>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
        <Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin size="large" />
        </Content>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
        <Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Result
            status="error"
            title="Survey Unavailable"
            subTitle={error}
            extra={
              <Button type="primary" onClick={() => navigate('/')}>
                Go Home
              </Button>
            }
          />
        </Content>
      </Layout>
    );
  }

  if (completed) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
        <Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Result
            status="success"
            title="Survey Completed Successfully!"
            subTitle={`Thank you, ${surveyData?.patient?.firstName} ${surveyData?.patient?.lastName}. Your health survey has been submitted and will be reviewed by your healthcare team.`}
            extra={
              <Button type="primary" onClick={() => window.close()}>
                Close Window
              </Button>
            }
          />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Header style={{ 
        background: '#fff', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FileTextOutlined style={{ fontSize: 24, color: '#1890ff', marginRight: 12 }} />
          <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
            Care360 Health Survey
          </Title>
        </div>
        <Select
          value={i18n.language}
          onChange={handleLanguageChange}
          style={{ width: 120 }}
        >
          <Option value="vi">Tiếng Việt</Option>
          <Option value="en">English</Option>
        </Select>
      </Header>
      
      <Content style={{ padding: '24px', maxWidth: 1000, margin: '0 auto', width: '100%' }}>
        <Card>
          <div id="public-survey-page">
            <div style={{ marginBottom: 24 }}>
              <Title level={4}>
                Health Survey for {surveyData?.patient?.firstName} {surveyData?.patient?.lastName}
              </Title>
              <Text type="secondary">
                Survey expires: {moment(surveyData?.expiresAt).format('MMMM DD, YYYY at HH:mm')}
              </Text>
            </div>

            <Alert
              message="Privacy Notice"
              description="Your responses will be securely transmitted to your healthcare team and stored in your medical record. This survey is confidential and will help your healthcare providers better understand your health status."
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Steps current={currentStep} style={{ marginBottom: 32 }}>
              {steps.map((step, index) => (
                <Step
                  key={index}
                  title={step.title}
                  description={step.content}
                  icon={step.icon}
                  status={validationErrors.length > 0 && index === currentStep ? 'error' : undefined}
                />
              ))}
            </Steps>

            {/* Progress Indicator */}
            {completionStatus.total > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Progress
                  percent={completionStatus.percentage}
                  size="small"
                  status={completionStatus.percentage === 100 ? 'success' : 'active'}
                  format={() => t('healthSurvey.validation.completionStatus', {
                    completed: completionStatus.completed,
                    total: completionStatus.total
                  })}
                />
              </div>
            )}

            {/* Validation Errors Display */}
            {validationErrors.length > 0 && (
              <Alert
                message={t('healthSurvey.validation.missingRequiredFields')}
                description={
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    {validationErrors.slice(0, 5).map((error, index) => (
                      <li key={index}>
                        {error.categoryTitle ? `${error.categoryTitle}: ${error.fieldName}` : error.fieldName}
                      </li>
                    ))}
                    {validationErrors.length > 5 && (
                      <li>...and {validationErrors.length - 5} more</li>
                    )}
                  </ul>
                }
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
                style={{ marginBottom: 32 }}
                closable
                onClose={() => setValidationErrors([])}
              />
            )}

            {renderStepContent()}
            {renderFooter()}
          </div>
        </Card>
      </Content>
    </Layout>
  );
};

export default PublicSurveyPage;
